import React, { useEffect, useMemo, useState } from "react";
import { Check, Feather } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

/**
 * MySignals — reflects the user's own recorded patterns back to them.
 *
 * Never fabricates insight. The API only surfaces items that have been
 * recorded at least twice; if the user has not recorded enough, we show
 * an invitation state instead.
 */
export default function MySignals({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState("");
  const [insightSaving, setInsightSaving] = useState(false);
  const [insightSaved, setInsightSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    endpoints
      .nsSignals()
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setInsight(r.data?.insight || "");
      })
      .catch(() => alive && setData({ entry_count: 0 }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const hasPatterns = useMemo(() => {
    if (!data) return false;
    return (
      (data.sensations?.length || 0) +
        (data.contexts?.length || 0) +
        (data.responses?.length || 0) +
        (data.supports?.length || 0) >
      0
    );
  }, [data]);

  const saveInsight = async () => {
    if (insightSaving) return;
    setInsightSaving(true);
    try {
      await endpoints.nsSaveInsight(insight);
      setInsightSaved(true);
      toast.success("Saved.");
      setTimeout(() => setInsightSaved(false), 2400);
    } catch {
      toast.error("Couldn't save just yet.");
    } finally {
      setInsightSaving(false);
    }
  };

  return (
    <section data-testid="ns-my-signals" className="reveal reveal-1">
      <div className="max-w-2xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          My Signals
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic">
          Learn what your body has been teaching you.
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
          This slowly reflects the patterns you have been recording — not a diagnosis, not a type. Only
          what you have actually noticed for yourself, over time.
        </p>
      </div>

      <div
        className="mt-8 p-8 md:p-12 rounded-[24px] border hairline bg-[hsl(var(--paper))]"
        data-testid="ns-my-signals-card"
      >
        {loading && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Reading your signals…</p>
        )}
        {!loading && !hasPatterns && (
          <p
            className="font-display italic text-lg md:text-xl text-[hsl(var(--fg))]/75 leading-snug max-w-2xl"
            data-testid="ns-my-signals-empty"
          >
            Your signals will become clearer as you spend time noticing them.
          </p>
        )}
        {!loading && hasPatterns && (
          <div className="space-y-8">
            <PatternBlock
              heading="When I'm under pressure, I notice:"
              items={data.sensations}
              testid="ns-signal-sensations"
            />
            <PatternBlock
              heading="When I feel this way, I've often noticed:"
              items={data.contexts}
              testid="ns-signal-contexts"
            />
            <PatternBlock
              heading="I tend to:"
              items={data.responses}
              testid="ns-signal-responses"
            />
            <PatternBlock
              heading="What often supports me:"
              items={data.supports}
              testid="ns-signal-supports"
            />
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-[hsl(var(--line))]">
          <label
            htmlFor="ns-insight-input"
            className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--copper))]"
          >
            What I'm learning
          </label>
          <Textarea
            id="ns-insight-input"
            data-testid="ns-insight-input"
            value={insight}
            onChange={(e) => setInsight(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="A sentence, in your own words. This is yours to write."
            className="mt-3 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
          />
          <div className="mt-4 flex items-center gap-3">
            <Button
              type="button"
              onClick={saveInsight}
              disabled={insightSaving}
              data-testid="ns-insight-save"
              variant="outline"
              className="rounded-full border-[hsl(var(--copper))]/40 text-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/10"
            >
              {insightSaved ? (
                <>
                  <Check size={13} className="mr-1.5" /> Saved
                </>
              ) : (
                <>
                  <Feather size={13} className="mr-1.5" />
                  {insightSaving ? "Saving…" : "Save"}
                </>
              )}
            </Button>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              {insight.length}/500
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatternBlock({ heading, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
        {heading}
      </div>
      <ul className="mt-3 flex flex-wrap gap-2">
        {items.map((it) => (
          <li
            key={it}
            className="px-3.5 py-1.5 rounded-full text-[13px] bg-[hsl(var(--sand))]/60 text-[hsl(var(--fg))]/85 border hairline"
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
